import { useEffect, useRef, useState } from "react";
import { SHOP_CANONICAL_HOST } from "../../../src/shop-hosts";

/** The one public address of a product — the same on web, in the app and in WhatsApp. No personal data, no query. */
export const productShareUrl = (handle: string): string => `https://${SHOP_CANONICAL_HOST}/p/${encodeURIComponent(handle)}`;

const ShareIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><path d="m8.6 13.5 6.8 4M15.4 6.5l-6.8 4" />
  </svg>
);

/** Share this product: the phone's own share sheet where there is one, else WhatsApp / Facebook / Copy link. */
export function ShareButton({ handle, title }: { handle: string; title: string }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const url = productShareUrl(handle);
  const text = `${title} at Z S MERCH`;

  useEffect(() => {
    if (!open) return;
    const away = (e: Event) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("pointerdown", away); document.removeEventListener("keydown", esc); };
  }, [open]);

  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 2500);
    return () => window.clearTimeout(t);
  }, [copied]);

  async function onShare() {
    if (typeof navigator.share === "function") {
      try { await navigator.share({ title, text, url }); } catch { /* dismissed the sheet */ }
      return;
    }
    setOpen((o) => !o);
  }

  async function copy() {
    try { await navigator.clipboard.writeText(url); } catch {
      const t = document.createElement("textarea");
      t.value = url; t.style.position = "fixed"; t.style.opacity = "0";
      document.body.appendChild(t); t.select();
      try { document.execCommand("copy"); } catch { /* nothing more to try */ }
      t.remove();
    }
    setCopied(true);
    setOpen(false);
  }

  return (
    <div className="share" ref={box}>
      <button type="button" className="btn btn-quiet share-btn" aria-haspopup="true" aria-expanded={open} aria-label={`Share ${title}`} onClick={onShare}>
        <ShareIcon /> Share
      </button>
      {open && (
        <ul className="share-menu" aria-label="Share this product">
          <li><a href={`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)}>WhatsApp</a></li>
          <li><a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)}>Facebook</a></li>
          <li><button type="button" onClick={copy}>Copy link</button></li>
        </ul>
      )}
      <span className="share-note" role="status" aria-live="polite">{copied ? "Link copied" : ""}</span>
    </div>
  );
}
